import { useEffect, useMemo } from 'react';
import { useDispatch } from 'react-redux';
import { Outlet, useLocation, useNavigate } from 'react-router-dom';
import Fab from '@mui/material/Fab';

import { Avatar, Box, Divider, Paper, Stack, Typography, useMediaQuery } from '@mui/material';
import { alpha, styled, useTheme } from '@mui/material/styles';

import { SET_MENU } from 'store/actions';
import Customization from '../Customization';
import Logo from 'ui-component/Logo';

import { Home, History, WalletCards, PhoneCall, ChevronRight, MessageCircle } from 'lucide-react';
import { brandConfig } from 'config/brand';
import { WhatsappRounded } from '@mui/icons-material';
import WhatsAppFloatingBtn from 'ui-component/WhatsAppFloatingBtn';

const MOBILE_NAV_HEIGHT = 84;
const DESKTOP_SIDEBAR_WIDTH = 280;

const Main = styled('main', {
    shouldForwardProp: (prop) => prop !== 'isMobile'
})(({ isMobile }) => ({
    flexGrow: 1,
    minHeight: '100vh',
    background: 'linear-gradient(180deg, #F5F7FB 0%, #EEF3F9 100%)',
    transition: 'all 0.25s ease',
    padding: isMobile ? '16px 12px 104px' : '24px 28px 32px',
    marginLeft: isMobile ? 0 : DESKTOP_SIDEBAR_WIDTH
}));

const navItems = [
    { label: 'Home', icon: Home, path: '/' },
    { label: 'History', icon: History, path: '/user/transactions' },
    { label: 'Service', icon: WalletCards, path: '/services' },
    { label: 'Contact us', icon: PhoneCall, path: '/more/support' }
];

const isPathActive = (pathname, path) => {
    if (path === '/') return pathname === '/';
    return pathname.startsWith(path);
};

const MainLayout = () => {
    const theme = useTheme();
    const navigate = useNavigate();
    const location = useLocation();
    const dispatch = useDispatch();

    const isMobile = useMediaQuery(theme.breakpoints.down('lg'));

    useEffect(() => {
        dispatch({ type: SET_MENU, opened: !isMobile });
    }, [dispatch, isMobile]);

    return (
        <Box sx={{ display: 'flex', minHeight: '100vh', backgroundColor: '#F5F7FB' }}>
            {!isMobile && (
                <Box
                    sx={{
                        position: 'fixed',
                        top: 0,
                        left: 0,
                        bottom: 0,
                        width: DESKTOP_SIDEBAR_WIDTH,
                        backgroundColor: 'rgba(255,255,255,0.92)',
                        backdropFilter: 'blur(18px)',
                        borderRight: `1px solid ${alpha('#0F172A', 0.06)}`,
                        px: 2,
                        py: 2.5,
                        overflowY: 'auto'
                    }}
                >
                    <Stack spacing={3}>
                        <Stack
                            direction="row"
                            alignItems="center"
                            spacing={1.5}
                            sx={{
                                px: 1,
                                py: 1
                            }}
                        >
                            <Box
                                sx={{
                                    width: 58,
                                    height: 58,
                                    borderRadius: '18px',
                                    background: 'linear-gradient(135deg, #ffffff 0%, #F2F6FF 100%)',
                                    border: `1px solid ${alpha('#0F172A', 0.06)}`,
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    overflow: 'hidden'
                                }}
                            >
                                <Logo width={44} />
                            </Box>

                            <Box sx={{ minWidth: 0 }}>
                                <Typography
                                    sx={{
                                        fontSize: 20,
                                        fontWeight: 800,
                                        color: '#111827',
                                        letterSpacing: '-0.04em',
                                        lineHeight: 1
                                    }}
                                >
                                    {brandConfig.companyName}
                                </Typography>
                                <Typography
                                    sx={{
                                        fontSize: 13,
                                        color: '#64748B',
                                        mt: 0.3
                                    }}
                                >
                                    Dashboard
                                </Typography>
                            </Box>
                        </Stack>

                        <Divider sx={{ borderColor: alpha('#0F172A', 0.06) }} />

                        <Box>
                            <Typography
                                sx={{
                                    px: 1,
                                    mb: 1.5,
                                    fontSize: 14,
                                    fontWeight: 700,
                                    color: '#111827'
                                }}
                            >
                                Menu
                            </Typography>

                            <Stack spacing={0.75}>
                                {navItems.map((item) => {
                                    const Icon = item.icon;
                                    const active = isPathActive(location.pathname, item.path);

                                    return (
                                        <Stack
                                            key={item.label}
                                            direction="row"
                                            alignItems="center"
                                            justifyContent="space-between"
                                            onClick={() => navigate(item.path)}
                                            sx={{
                                                px: 1.5,
                                                py: 1.4,
                                                borderRadius: '18px',
                                                cursor: 'pointer',
                                                transition: 'all 0.2s ease',
                                                backgroundColor: active ? alpha('#6D4AFF', 0.12) : 'transparent',
                                                color: active ? '#111827' : '#1F2937',
                                                '&:hover': {
                                                    backgroundColor: active ? alpha('#6D4AFF', 0.14) : alpha('#0F172A', 0.04)
                                                }
                                            }}
                                        >
                                            <Stack direction="row" alignItems="center" spacing={1.25}>
                                                <Box
                                                    sx={{
                                                        width: 34,
                                                        height: 34,
                                                        borderRadius: '12px',
                                                        display: 'flex',
                                                        alignItems: 'center',
                                                        justifyContent: 'center',
                                                        color: active ? '#6D4AFF' : '#111827'
                                                    }}
                                                >
                                                    <Icon size={19} strokeWidth={2.2} />
                                                </Box>

                                                <Typography
                                                    sx={{
                                                        fontSize: 15,
                                                        fontWeight: active ? 700 : 500
                                                    }}
                                                >
                                                    {item.label}
                                                </Typography>
                                            </Stack>

                                            {active && <ChevronRight size={16} color="#6D4AFF" />}
                                        </Stack>
                                    );
                                })}
                            </Stack>
                        </Box>
                    </Stack>
                </Box>
            )}

            <Main isMobile={isMobile}>
                <Box
                    sx={{
                        width: '100%',
                        maxWidth: isMobile ? '100%' : '1400px',
                        mx: 'auto'
                    }}
                >
                    <Outlet />
                </Box>
            </Main>

            {isMobile && (
                <Paper
                    elevation={0}
                    sx={{
                        position: 'fixed',
                        left: 0,
                        right: 0,
                        bottom: 0,
                        zIndex: 1300,
                        height: MOBILE_NAV_HEIGHT,
                        borderTop: `1px solid ${alpha('#0F172A', 0.08)}`,
                        backgroundColor: 'rgba(255,255,255,0.96)',
                        backdropFilter: 'blur(18px)',
                        borderRadius: 0,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        px: 1
                    }}
                >
                    <Stack direction="row" alignItems="center" justifyContent="space-around" sx={{ width: '100%', maxWidth: 560 }}>
                        {navItems.map((item) => {
                            const Icon = item.icon;
                            const active = isPathActive(location.pathname, item.path);

                            return (
                                <Stack
                                    key={item.label}
                                    alignItems="center"
                                    spacing={0.6}
                                    onClick={() => navigate(item.path)}
                                    sx={{
                                        minWidth: 68,
                                        cursor: 'pointer',
                                        color: active ? '#0D4CA6' : '#7B8190'
                                    }}
                                >
                                    <Box
                                        sx={{
                                            width: 42,
                                            height: 42,
                                            borderRadius: '14px',
                                            display: 'flex',
                                            alignItems: 'center',
                                            justifyContent: 'center',
                                            backgroundColor: active ? alpha('#0D4CA6', 0.1) : 'transparent'
                                        }}
                                    >
                                        <Icon size={22} strokeWidth={active ? 2.7 : 2.2} />
                                    </Box>

                                    <Typography
                                        sx={{
                                            fontSize: 12.5,
                                            fontWeight: active ? 700 : 500
                                        }}
                                    >
                                        {item.label}
                                    </Typography>
                                </Stack>
                            );
                        })}
                    </Stack>
                </Paper>
            )}

            <Customization />
            <WhatsAppFloatingBtn />
        </Box>
    );
};

export default MainLayout;
